import { type ReactElement } from 'react';
import {
  PolicyMode,
  type PolicyModeType,
  type EnforceDisabledReason,
} from '@kirin/api-client/contracts/policies';
import { type CatalogIdType } from '@kirin/api-client/contracts/ai-instruction-catalog';
import { PolicyModeSelector } from '@/components/policylist/components/PolicyModeSelector';
import { AiInstructionCatalogPage } from './AiInstructionCatalogPage';

export interface CatalogModePageProps {
  /** Which catalog the page renders. */
  catalog: CatalogIdType;
  /** Org scope when set; personal (account) scope when undefined. */
  organizationId?: string;
  /** Whether the viewer may change the mode / edit categories. */
  canEdit: boolean;
  /** Current mode of the catalog's policy (Off/Monitor; Enforce is greyed). */
  mode: PolicyModeType;
  /**
   * Whether Enforce is selectable — comes from the policy's API-adjusted
   * `supportsEnforce` (the backend greys it when the `enforce_policy_mode` flag
   * is off, or when the policy does not support Enforce — every catalog policy
   * today, KIR-5037). Must NOT be hard-coded, or the UI would offer an Enforce
   * the backend refuses or silently coerces to Monitor.
   */
  supportsEnforce: boolean;
  /** Why Enforce is disabled (feature-flag vs coming-soon), for the tooltip. */
  enforceDisabledReason?: EnforceDisabledReason;
  /** Optional Enforce info tooltip, mirroring the Policies-page card. */
  enforceTooltip?: string;
  /** Persist a new mode (wired to the policy-toggle mutation by the caller). */
  onModeChange: (mode: PolicyModeType) => void;
  /** Whether a mode change is in flight. */
  modePending: boolean;
}

/**
 * KIR-3816 / KIR-3925: shared shell for a catalog page with an Off/Monitor
 * mode control (default Off; Enforce shown greyed "Coming Soon" until it is
 * implemented, KIR-5037) above the framework tabs. Each
 * catalog page (Secure AI Coding Enforcement, Threat Modeling) is a thin call
 * site that picks the catalog; the mode drives that catalog's scanner-less
 * policy. Enforce availability follows the policy's API `supportsEnforce`
 * (same gating as the Policies-page cards), so the control never offers an
 * Enforce the backend would refuse or silently downgrade.
 */
export function CatalogModePage({
  catalog,
  organizationId,
  canEdit,
  mode,
  supportsEnforce,
  enforceDisabledReason,
  enforceTooltip,
  onModeChange,
  modePending,
}: CatalogModePageProps): ReactElement {
  return (
    <AiInstructionCatalogPage
      organizationId={organizationId}
      canEdit={canEdit}
      catalog={catalog}
      // The shell PageHeader already names these top-level pages (KIR-5600).
      showTitle={false}
      modeControl={
        <PolicyModeSelector
          value={mode ?? PolicyMode.Off}
          onChange={onModeChange}
          disabled={!canEdit || modePending}
          supportsEnforce={supportsEnforce}
          enforceDisabledReason={enforceDisabledReason}
          enforceTooltip={enforceTooltip}
        />
      }
    />
  );
}
